// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/Content/LexicalEditor.js
import React, { useCallback, useRef, useEffect } from 'react';
import {
  DecoratorNode, $createParagraphNode, $createTextNode, $getRoot
} from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import { QuoteNode, HeadingNode } from '@lexical/rich-text';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListNode, ListItemNode } from '@lexical/list';
import { CodeNode } from '@lexical/code';
import { LinkNode } from '@lexical/link';
import { TableNode, TableCellNode, TableRowNode } from '@lexical/table';
import { $generateNodesFromDOM } from '@lexical/html';
import debounce from 'lodash/debounce';
import Toolbar from '../LexicalEditor/Toolbar';
import DragDrop from '../../components/LexicalEditor/DragDrop.js';
import TablePlugin from '../LexicalEditor/LexicalEditorTableUtils.jsx';
import '../../css/LexicalEditor.scss';

// ImageNode
export class ImageNode extends DecoratorNode {
  constructor(src, altText, key) {
    super(key);
    this.__src = src;
    this.__altText = altText;
  }

  static getType() {
    return 'image';
  }

  static clone(node) {
    return new ImageNode(node.__src, node.__altText, node.__key);
  }

  static importDOM() {
    return {
      img: (node) => ({
        conversion: (domNode) => {
          const src = domNode.getAttribute('src');
          const altText = domNode.getAttribute('alt') || 'Image';
          if (!src) {
            return null;
          }
          return {
            node: $createImageNode({ src, altText }),
          };
        },
        priority: 1,
      }),
    };
  }

  createDOM() {
    const img = document.createElement('img');
    img.src = this.__src;
    img.alt = this.__altText;
    img.setAttribute('draggable', 'true');
    img.setAttribute('data-key', this.getKey());
    img.style.maxWidth = '100%';
    img.style.cursor = 'move';
    img.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
    return img;
  }

  updateDOM(prevNode, dom) {
    if (prevNode.__src !== this.__src) dom.src = this.__src;
    if (prevNode.__altText !== this.__altText) dom.alt = this.__altText;
    return false;
  }

  exportJSON() {
    return { src: this.__src, altText: this.__altText, type: 'image', version: 1 };
  }

  static importJSON(serializedNode) {
    return $createImageNode({ src: serializedNode.src, altText: serializedNode.altText });
  }

  decorate() {
    return (
      <img
        src={this.__src}
        alt={this.__altText}
        data-key={this.getKey()}
        style={{ maxWidth: '100%', cursor: 'move', transition: 'opacity 0.2s ease, transform 0.2s ease' }}
        draggable="true"
      />
    );
  }
}

export function $createImageNode({ src, altText, key }) {
  return new ImageNode(src, altText, key);
}

// Main LexicalEditor Component
const LexicalEditor = ({ fieldName, label, placeholder, onChange, onImageUpload, isUploading, setIsUploading, initialValue }) => {
  const debouncedOnChange = useCallback(
    debounce((fieldName, htmlValue) => {
      onChange(fieldName, htmlValue);
    }, 300),
    [onChange]
  );

  const onEditorChange = (editorState, editor) => {
    editorState.read(() => {
      const htmlValue = $generateHtmlFromNodes(editor);
      debouncedOnChange(fieldName, htmlValue);
    });
  };

  const onError = (error) => {
    console.error('Lexical Error:', error);
  };

  const initialConfig = {
    namespace: `${fieldName}Editor`,
    onError,
    nodes: [
      ImageNode, ListNode, ListItemNode, CodeNode, LinkNode, QuoteNode, HeadingNode,
      TableNode, TableCellNode, TableRowNode
    ],
    theme: {
      text: {
        underline: 'underline',
      },
      table: 'lexical-table',
      tableCell: 'lexical-table-cell',
      tableCellHeader: 'lexical-table-cell-header',
      tableRow: 'lexical-table-row',
    },
    editorState: (editor) => {
      const root = $getRoot();
      console.log(`Initial value for ${fieldName}:`, initialValue);

      if (initialValue && typeof initialValue === 'string' && initialValue.trim() !== '') {
        try {
          const wrappedHtml = `<!DOCTYPE html><html><body>${initialValue}</body></html>`;
          const parser = new DOMParser();
          const dom = parser.parseFromString(wrappedHtml, 'text/html');

          if (!dom || !dom.body) {
            throw new Error('Invalid HTML: No body element found');
          }

          const nodes = $generateNodesFromDOM(editor, dom);
          console.log(`Nodes generated for ${fieldName}:`, nodes);

          if (nodes.length === 0) {
            throw new Error('No valid nodes generated from HTML');
          }

          root.clear();
          nodes.forEach((node) => {
            const nodeType = node.getType();
            console.log(`Processing node type for ${fieldName}:`, nodeType, node);

            if (
              nodeType === 'paragraph' ||
              nodeType === 'text' ||
              nodeType === 'image' ||
              nodeType === 'list' ||
              nodeType === 'listitem' ||
              nodeType === 'code' ||
              nodeType === 'link' ||
              nodeType === 'quote' ||
              nodeType === 'heading' ||
              nodeType === 'linebreak' ||
              nodeType === 'table' ||
              nodeType === 'tablerow' ||
              nodeType === 'tablecell'
            ) {
              if (nodeType === 'paragraph' && node.getDirection) {
                node.setDirection(null);
              }
              root.append(node);
            } else {
              console.warn(`Skipping unsupported node type for ${fieldName}:`, nodeType);
            }
          });

          if (root.getChildren().length === 0) {
            const paragraph = $createParagraphNode();
            root.append(paragraph);
          }
        } catch (error) {
          console.error(`Error parsing initialValue for ${fieldName}:`, error);
          root.clear();
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = initialValue;
          const textContent = tempDiv.textContent || tempDiv.innerText || '';
          const paragraph = $createParagraphNode();
          paragraph.append($createTextNode(textContent));
          root.append(paragraph);
        }
      } else {
        root.clear();
        const paragraph = $createParagraphNode();
        root.append(paragraph);
      }
    },
  };

    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
          if (wrapperRef.current) {
            const wrapperRect = wrapperRef.current.getBoundingClientRect();
            const toolbar = wrapperRef.current.querySelector('.editor-toolbar');

            if (toolbar) {
                if (wrapperRect.top < 0) {
                    toolbar.style.position = 'fixed';
                    toolbar.style.top = '0';
                    toolbar.style.width = `${wrapperRect.width}px`;

                    if (wrapperRect.bottom < toolbar.offsetHeight) {
                        toolbar.style.top = `${wrapperRect.bottom - toolbar.offsetHeight}px`;
                    }
                } else {
                    toolbar.style.position = 'relative';
                    toolbar.style.top = 'auto';
                    toolbar.style.width = 'auto';
                }
            }
          }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
      }, []);

  return (
    <div className="lexical-editor-wrapper" ref={wrapperRef}>
      <label htmlFor={fieldName} className="form-label">{label}</label>
      <LexicalComposer initialConfig={initialConfig}>
        <Toolbar fieldName={fieldName} onImageUpload={onImageUpload} isUploading={isUploading} setIsUploading={setIsUploading} />
        <RichTextPlugin
          contentEditable={<ContentEditable className="custom-editor p-editor-content" />}
          placeholder={<div className="editor-placeholder">{placeholder}</div>}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <OnChangePlugin onChange={onEditorChange} />
        <HistoryPlugin />
        <TablePlugin />
        <DragDrop fieldName={fieldName} onImageUpload={onImageUpload} isUploading={isUploading} setIsUploading={setIsUploading} />
      </LexicalComposer>
    </div>
  );
};

export default LexicalEditor;